import { Preferences } from '@capacitor/preferences';
import { Capacitor } from '@capacitor/core';

const KEYS = {
  EMAIL: 'savedEmail',
  PASSWORD: 'savedPassword',
  DECLINED: 'declinedRemember',
};

/**
 * Save user credentials securely via Capacitor Preferences.
 * On iOS, Preferences uses the native Keychain-backed storage (encrypted at rest).
 */
export const saveCredentials = async (email: string, password: string): Promise<void> => {
  await Preferences.set({ key: KEYS.EMAIL, value: email });
  await Preferences.set({ key: KEYS.PASSWORD, value: password });
  // Clear any previous decline flag since user chose to save
  await Preferences.remove({ key: KEYS.DECLINED });
};

/**
 * Load saved credentials. Returns null if none are stored.
 */
export const loadCredentials = async (): Promise<{ email: string; password: string } | null> => {
  const { value: email } = await Preferences.get({ key: KEYS.EMAIL });
  const { value: password } = await Preferences.get({ key: KEYS.PASSWORD });

  if (email && password) {
    return { email, password };
  }
  return null;
};

/**
 * Clear all saved credentials and the declined flag.
 */
export const clearCredentials = async (): Promise<void> => {
  await Preferences.remove({ key: KEYS.EMAIL });
  await Preferences.remove({ key: KEYS.PASSWORD });
  await Preferences.remove({ key: KEYS.DECLINED });
};

/**
 * Check if the user has previously declined saving credentials.
 */
export const hasDeclinedRemember = async (): Promise<boolean> => {
  const { value } = await Preferences.get({ key: KEYS.DECLINED });
  return value === 'true';
};

/**
 * Mark that the user has declined saving credentials.
 */
export const setDeclinedRemember = async (): Promise<void> => {
  await Preferences.set({ key: KEYS.DECLINED, value: 'true' });
};

/**
 * Check if credentials are currently saved.
 */
export const hasStoredCredentials = async (): Promise<boolean> => {
  const creds = await loadCredentials();
  return creds !== null;
};

/**
 * Whether the remember credentials feature is available (native platforms only).
 * On web, browsers handle credential autofill natively.
 */
export const isCredentialStorageAvailable = (): boolean => {
  return Capacitor.isNativePlatform();
};
